import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import signalRecommenderI18nKeys from "../../utils/i18n";

import { StyledCategoryTrigger, StyledTriggerValue } from "./styles";

type SignalFilterCategoryTriggerProps = {
    readonly title: string;
    readonly selectedCount: number;
    readonly isOpen: boolean;
    readonly isDisabled: boolean;
    readonly dataAutomation: string;
};

/**
 * The filter card that opens a category's tree.
 *
 * Deliberately not a button and containing none: it is the popover's trigger content, and the toggle
 * handler lives on the slot above it — so an inner control would only add a second tab stop competing
 * for the same click.
 */
export const SignalFilterCategoryTrigger = ({
    title,
    selectedCount,
    isOpen,
    isDisabled,
    dataAutomation,
}: SignalFilterCategoryTriggerProps) => {
    const { translate } = useTranslate();

    const hasSelection = selectedCount > 0;
    const selected = signalRecommenderI18nKeys.confirm.filters.selected({ count: selectedCount });

    return (
        <StyledCategoryTrigger
            data-automation={dataAutomation}
            $isOpen={isOpen}
            $isDisabled={isDisabled}
            alignItems="center"
            justifyContent="space-between"
        >
            <Flex
                alignItems="center"
                gap="8px"
                minWidth="0"
            >
                {title}

                {hasSelection ? null : (
                    <Text
                        fontVariant="tooltip"
                        color={colorsPalettes.carbon[250]}
                    >
                        {translate(signalRecommenderI18nKeys.confirm.filters.notSelected)}
                    </Text>
                )}
            </Flex>

            <StyledTriggerValue
                alignItems="center"
                gap="4px"
            >
                {hasSelection ? translate(selected.key, selected.replacements) : null}
                <SWReactIcons
                    iconName={isOpen ? "chev-up" : "chev-down"}
                    size="xs"
                    iconColor={isDisabled ? colorsPalettes.carbon[200] : colorsPalettes.carbon[400]}
                />
            </StyledTriggerValue>
        </StyledCategoryTrigger>
    );
};
